import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, Timer, AlertCircle, Gift, Pencil, Trash2 } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/lib/format";

const initials = (name) =>
  (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const scoreColor = (score) => {
  if (score === null || score === undefined) return "#a8a29e";
  if (score >= 80) return "#059669";
  if (score >= 50) return "#d97706";
  return "#e11d48";
};

/**
 * FASE 19 — team punctuality qualification (admin/manager dashboard):
 * table + chart of collaborators with their checklist punctuality scores.
 */
export default function TeamScores() {
  const [data, setData] = useState(null);
  const [prize, setPrize] = useState(null);
  const [prizeOpen, setPrizeOpen] = useState(false);
  const [prizeForm, setPrizeForm] = useState({ title: "", description: "" });

  const loadPrize = () =>
    api
      .get("/gamification/prize")
      .then(({ data }) => setPrize(data.prize))
      .catch(() => {});

  useEffect(() => {
    api
      .get("/dashboard/team-scores")
      .then(({ data }) => setData(data))
      .catch((err) => console.error("team-scores failed", err));
    loadPrize();
  }, []);

  const savePrize = async () => {
    if (!prizeForm.title.trim()) {
      toast.error("Informe o título do prêmio");
      return;
    }
    try {
      await api.put("/gamification/prize", prizeForm);
      toast.success("Prêmio do mês definido! A equipe já pode ver no dashboard.");
      setPrizeOpen(false);
      loadPrize();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar prêmio");
    }
  };

  const removePrize = async () => {
    try {
      await api.delete("/gamification/prize");
      toast.success("Prêmio removido");
      setPrizeOpen(false);
      loadPrize();
    } catch (err) {
      toast.error("Falha ao remover prêmio");
    }
  };

  if (!data) return null;
  const scores = data.scores || [];
  const scored = scores.filter((s) => s.score !== null && s.score !== undefined);
  const chartData = scored.slice(0, 12).map((s) => ({
    name: (s.name || "").split(" ")[0].slice(0, 12),
    score: s.score,
  }));

  return (
    <section data-testid="team-scores">
      <div className="mb-3 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Qualidade da equipe
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            Pontualidade dos colaboradores (30 dias)
          </h3>
          <p className="text-xs text-stone-500 font-body mt-0.5">
            No prazo = 100 pts · Atrasado = 50 pts · Não executado = 0 pts
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setPrizeForm({
              title: prize?.title || "",
              description: prize?.description || "",
            });
            setPrizeOpen(true);
          }}
          data-testid="prize-set-btn"
        >
          <Gift className="h-3.5 w-3.5 mr-1.5 text-amber-600" />
          {prize ? "Editar prêmio do mês" : "Definir prêmio do mês"}
        </Button>
      </div>

      {/* FASE 21 — current prize banner */}
      {prize && (
        <div
          className="mb-4 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5"
          data-testid="prize-banner"
        >
          <Gift className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-amber-800">
              Prêmio do mês para o 1º lugar: {prize.title}
            </div>
            {prize.description && (
              <div className="text-xs text-amber-700/80 font-body">{prize.description}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setPrizeForm({ title: prize.title, description: prize.description || "" });
              setPrizeOpen(true);
            }}
            className="text-amber-600 hover:text-amber-800"
            data-testid="prize-edit-btn"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      )}

      <Dialog open={prizeOpen} onOpenChange={setPrizeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Prêmio do mês 🏆</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500 font-body -mt-2">
            Defina o prêmio para o 1º lugar do ranking de XP do mês. Ele será
            exibido no dashboard de toda a equipe.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Prêmio *</Label>
              <Input
                value={prizeForm.title}
                onChange={(e) => setPrizeForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Vale-presente de R$ 200"
                className="mt-1 bg-white"
                data-testid="prize-title-input"
              />
            </div>
            <div>
              <Label>Detalhes (opcional)</Label>
              <Input
                value={prizeForm.description}
                onChange={(e) => setPrizeForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Ex: Entregue na reunião de fechamento do mês"
                className="mt-1 bg-white"
                data-testid="prize-description-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {prize && (
              <Button variant="ghost" onClick={removePrize} className="text-rose-600" data-testid="prize-delete-btn">
                <Trash2 className="h-4 w-4 mr-1" /> Remover
              </Button>
            )}
            <Button onClick={savePrize} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prize-save-btn">
              Salvar prêmio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden" data-testid="team-scores-table">
          {scores.length === 0 ? (
            <div className="p-6 text-center text-sm text-stone-500 font-body">
              Nenhum colaborador para avaliar ainda.
            </div>
          ) : (
            <div className="divide-y divide-stone-100 max-h-[340px] overflow-y-auto">
              {scores.map((s, idx) => (
                <div key={s.user_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`team-score-row-${s.user_id}`}>
                  <span className="text-xs font-bold text-stone-400 w-5">{idx + 1}º</span>
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={s.picture || ""} alt={s.name} />
                    <AvatarFallback className="text-[10px] bg-emerald-700 text-white">
                      {initials(s.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{s.name}</div>
                    <div className="text-[10px] text-stone-500 font-body uppercase">{ROLE_LABEL[s.role] || s.role}</div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-body text-stone-500">
                    <span className="inline-flex items-center gap-0.5 text-emerald-700">
                      <Trophy className="h-3 w-3" /> {s.on_time}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-amber-600">
                      <Timer className="h-3 w-3" /> {s.late}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-rose-600">
                      <AlertCircle className="h-3 w-3" /> {s.missed}
                    </span>
                  </div>
                  {s.score === null || s.score === undefined ? (
                    <Badge variant="outline" className="border-stone-300 text-stone-500 text-[10px]">
                      Sem dados
                    </Badge>
                  ) : (
                    <Badge
                      className="text-white border-none text-xs font-bold min-w-[52px] justify-center"
                      style={{ backgroundColor: scoreColor(s.score) }}
                      data-testid={`team-score-value-${s.user_id}`}
                    >
                      {s.score}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-xl border-stone-200 bg-white" data-testid="team-scores-chart">
          {chartData.length === 0 ? (
            <div className="text-sm text-stone-500 font-body py-10 text-center">
              O gráfico aparecerá quando houver execuções pontuadas.
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v} pts`, "Pontualidade"]} />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={36}>
                    {chartData.map((entry, i) => (
                      <Cell key={`${entry.name}-${i}`} fill={scoreColor(entry.score)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
